import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import { createContext } from "react";
import { ListenEvents, eventListeners } from "./eventListeners";
import { STAGEDOOR_ROOT } from "./constants";
import {
  DeviceInfo,
  NetworkDiagnosticReport,
} from "@wolzienllc/vcc-react-commons";
import { store } from "../state";
import { setError, setSocketReconnecting } from "../state/actions";
import { PageError, PageErrorType } from "../state/types";

interface EmitEvents {
  networkDiagnosticResult: (data: {
    error?: Error;
    report?: NetworkDiagnosticReport;
  }) => void;
  vipFacetimeUpdate: (data: { facetime: string }) => void;
  zoomNotAvailable: (data: {}) => void;
}
type EmitEventNames = keyof EmitEvents;
type EmitEventParams = Parameters<EmitEvents[EmitEventNames]>;

/**
 * The server rejects a handshake while the previous socket is still counted as
 * connected. Socket.io does not retry a middleware rejection on its own, so
 * without these retries the page freezes with no message and the guest reloads,
 * which is what produces the "Your Invitation is Invalid or In Use" report.
 * Retry for longer than the server pingTimeout of 60 seconds.
 */
const MAX_HANDSHAKE_RETRIES = 12;
const HANDSHAKE_RETRY_DELAY_MS = 3000;

/**
 * Maps a socket middleware rejection to a page error. The middleware sends its
 * cause as the error message, so match on that rather than assuming "in use".
 */
const getHandshakeError = (err: Error): PageError => {
  const message = err?.message ?? "";

  if (message.includes("no longer valid")) {
    return { type: PageErrorType.InvitationCancelled };
  }

  if (message.includes("wasn't found")) {
    return { type: PageErrorType.InvitationInvalid };
  }

  if (message.includes("already active")) {
    return { type: PageErrorType.InvitationInUse };
  }

  return { message };
};

export class SocketService {
  private socket?: Socket<ListenEvents, EmitEvents>;
  private handshakeRetries = 0;
  private retryTimer?: ReturnType<typeof setTimeout>;

  public init(sessionGuid: string, deviceInfo: DeviceInfo): void {
    //console.log(`socketService: init`);
    this.handshakeRetries = 0;

    this.socket = io(STAGEDOOR_ROOT, {
      reconnection: true,
      secure: true,
      query: {
        appName: "stageDoor",
        deviceInfo: deviceInfo
          ? JSON.stringify(deviceInfo)
          : JSON.stringify({}),
        sessionGuid,
      },
    });

    this.attachEventListeners();
    this.attachHandshakeRetry();
  }

  private attachHandshakeRetry(): void {
    if (!this.socket) {
      return;
    }

    this.socket.on("connect", () => {
      this.handshakeRetries = 0;
      store.dispatch(setSocketReconnecting(false));
    });

    this.socket.on("connect_error", (err: Error) => {
      // socket.active is true for a transport failure, which socket.io retries
      // by itself. It is false for a middleware rejection, which it does not.
      if (this.socket?.active) {
        return;
      }

      if (this.handshakeRetries >= MAX_HANDSHAKE_RETRIES) {
        store.dispatch(setSocketReconnecting(false));
        // Report the fault the server named. Not every refusal is "in use"; a
        // producer can cancel the invitation while the guest is connected.
        store.dispatch(setError(getHandshakeError(err)));
        return;
      }

      this.handshakeRetries += 1;
      store.dispatch(setSocketReconnecting(true));

      console.warn(
        `StageDoor handshake refused (${err.message}); retry ${this.handshakeRetries} of ${MAX_HANDSHAKE_RETRIES}.`
      );

      this.retryTimer = setTimeout(() => {
        this.socket?.connect();
      }, HANDSHAKE_RETRY_DELAY_MS);
    });
  }

  private attachEventListeners(): void {
    //console.log(`socketService: attachEventListeners`);
    if (this.socket) {
      Object.entries(eventListeners).forEach(([event, listener]) => {
        /*console.log("Got socket event");
        console.dir(event);*/
        this.socket.on(event, listener);
      });
    }
  }

  private detachEventListeners(): void {
    //console.log(`socketService: detachEventListeners`);
    if (this.socket) {
      Object.entries(eventListeners).forEach(([event, listener]) => {
        this.socket.off(event, listener);
      });
    }
  }

  public emit(event: keyof EmitEvents, ...args: EmitEventParams): void {
    this.socket?.emit(event, ...args);
  }

  public disconnect(): void {
    //console.log(`socketService: disconnect`);
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    this.detachEventListeners();
    this.socket?.disconnect();
    this.socket = undefined;
  }
}

export const SocketContext = createContext(new SocketService());
